/**
 * 虎鸡虫棒棋游戏逻辑
 * 7×7棋盘，虎吃鸡，鸡啄虫，虫蛀棒，棒打虎的单向克制循环
 * 胜负判定：攻入对方大本营
 */

import {
  Player,
  Position,
  TigerChickenGameState,
  TigerChickenPiece,
  TigerChickenPieceType,
  TigerChickenMove,
  TIGER_CHICKEN_PIECE_VALUES,
  TIGER_CHICKEN_PIECE_NAMES
} from '../types/game';

// ==================== 基础工具函数 ====================

/**
 * 检查位置是否在棋盘内
 */
export const isValidPosition = (position: Position): boolean => {
  return position.row >= 0 && position.row < 7 && position.col >= 0 && position.col < 7;
};

/**
 * 检查位置是否在大本营
 */
export const isHomePosition = (position: Position, player: Player): boolean => {
  if (player === 'black') {
    // 黑方大本营在底边中点 (6, 3)
    return position.row === 6 && position.col === 3;
  } else if (player === 'white') {
    // 白方大本营在顶边中点 (0, 3)
    return position.row === 0 && position.col === 3;
  }
  return false;
};

/**
 * 检查两个位置是否在同一线上（横、竖、对角线）
 * 注意：所有棋子都只能使用棋盘上实际画出的连线
 * 棋盘上的连线包括：横线、竖线、两条主对角线、四条辅助对角线
 */
export const isSameLine = (from: Position, to: Position, piece?: TigerChickenPiece): boolean => {
  // 横线
  if (from.row === to.row) return true;
  // 竖线
  if (from.col === to.col) return true;

  // 检查是否在同一条对角线上
  const fromDiff = from.row - from.col;
  const toDiff = to.row - to.col;
  const fromSum = from.row + from.col;
  const toSum = to.row + to.col;

  // 主对角线：左上到右下 (0,0) -> (6,6)，即 row - col = 0
  if (fromDiff === toDiff && fromDiff === 0) return true;

  // 副对角线：右上到左下 (0,6) -> (6,0)，即 row + col = 6
  if (fromSum === toSum && fromSum === 6) return true;

  // 以下4条辅助对角线在棋盘上实际存在，所有棋子都可以使用

  // 左上角到中心下方对角线：(4,0) -> (6,2)
  // 这条线的特征：row - col = 4
  // 验证：点(4,0): 4-0=4, 点(6,2): 6-2=4
  if (fromDiff === 4 && toDiff === 4) {
    // 额外验证：确保点确实在这条线的有效范围内
    if (isValidPointOnDiagonal4(from) && isValidPointOnDiagonal4(to)) {
      return true;
    }
  }

  // 左下角到中心上方对角线：(2,0) -> (0,2)
  // 这条线的特征：row + col = 2 且 row <= 2, col >= 0
  // 验证：点(2,0): 2+0=2, 点(0,2): 0+2=2
  if ((from.row + from.col) === 2 && (to.row + to.col) === 2) {
    if (isValidPointOnDiagonalNegative2(from) && isValidPointOnDiagonalNegative2(to)) {
      return true;
    }
  }

  // 右上角到中心下方对角线：(2,6) -> (0,4)
  // 这条线的特征：row - col = -4 (即 col - row = 4)
  // 验证：点(2,6): 2-6=-4, 点(0,4): 0-4=-4
  // 或者：row + col 的值从8递减到4
  if ((from.row - from.col) === -4 && (to.row - to.col) === -4) {
    if (isValidPointOnSum8(from) && isValidPointOnSum8(to)) {
      return true;
    }
  }

  // 右下角到中心上方对角线：(4,6) -> (6,4)
  // 这条线的特征：row + col = 10
  // 验证：点(4,6): 4+6=10, 点(6,4): 6+4=10
  if (fromSum === 10 && toSum === 10) {
    if (isValidPointOnSum10(from) && isValidPointOnSum10(to)) {
      return true;
    }
  }

  return false;
};

// 辅助函数：验证点是否在有效的对角线段上
const isValidPointOnDiagonal4 = (pos: Position): boolean => {
  // 左上角到中心下方对角线：(4,0) -> (6,2)
  // 这条线上的有效点：(4,0), (5,1), (6,2)
  return (pos.row === 4 && pos.col === 0) ||
         (pos.row === 5 && pos.col === 1) ||
         (pos.row === 6 && pos.col === 2);
};

const isValidPointOnDiagonalNegative2 = (pos: Position): boolean => {
  // 左下角到中心上方对角线：(2,0) -> (0,2)
  // 这条线上的有效点：(2,0), (1,1), (0,2)
  return (pos.row === 2 && pos.col === 0) ||
         (pos.row === 1 && pos.col === 1) ||
         (pos.row === 0 && pos.col === 2);
};

const isValidPointOnSum8 = (pos: Position): boolean => {
  // 右上角到中心下方对角线：(2,6) -> (0,4)
  // 这条线上的有效点：(2,6), (1,5), (0,4)
  return (pos.row === 2 && pos.col === 6) ||
         (pos.row === 1 && pos.col === 5) ||
         (pos.row === 0 && pos.col === 4);
};

const isValidPointOnSum10 = (pos: Position): boolean => {
  // 右下角到中心上方对角线：(4,6) -> (6,4)
  // 这条线上的有效点：(4,6), (5,5), (6,4)
  return (pos.row === 4 && pos.col === 6) ||
         (pos.row === 5 && pos.col === 5) ||
         (pos.row === 6 && pos.col === 4);
};

/**
 * 计算两个位置之间的距离
 */
export const getDistance = (from: Position, to: Position): number => {
  return Math.max(Math.abs(to.row - from.row), Math.abs(to.col - from.col));
};

/**
 * 检查移动是否为相邻移动（只能走一格）
 */
export const isAdjacentMove = (from: Position, to: Position): boolean => {
  const rowDiff = Math.abs(to.row - from.row);
  const colDiff = Math.abs(to.col - from.col);

  // 横向或纵向移动一格
  if ((rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1)) {
    return true;
  }

  // 对角线移动一格（需要起点和终点在对角线上）
  if (rowDiff === 1 && colDiff === 1) {
    // 使用 isSameLine 函数检查是否在同一条对角线上（不传入棋子信息，允许所有对角线）
    return isSameLine(from, to);
  }

  return false;
};

/**
 * 检查路径上是否有阻挡
 */
export const isPathBlocked = (
  board: (TigerChickenPiece | null)[][],
  from: Position,
  to: Position
): boolean => {
  const distance = getDistance(from, to);

  if (distance <= 1) return false; // 相邻位置无阻挡

  const rowStep = to.row === from.row ? 0 : (to.row - from.row) / Math.abs(to.row - from.row);
  const colStep = to.col === from.col ? 0 : (to.col - from.col) / Math.abs(to.col - from.col);

  let currentRow = from.row + rowStep;
  let currentCol = from.col + colStep;

  while (currentRow !== to.row || currentCol !== to.col) {
    if (board[currentRow][currentCol] !== null) {
      return true;
    }
    currentRow += rowStep;
    currentCol += colStep;
  }

  return false;
};

// ==================== 棋盘和棋子设置 ====================

/**
 * 创建初始虎鸡虫棒棋盘 (7×7)
 */
export const createTigerChickenBoard = (): (TigerChickenPiece | null)[][] => {
  const board: (TigerChickenPiece | null)[][] = Array(7).fill(null).map(() => Array(7).fill(null));

  // 放置黑方棋子（下方）
  // 第一行（底部）- 大本营
  board[6][0] = { type: 'tiger', player: 'black' }; // 大本营放置虎
  board[6][2] = { type: 'chicken', player: 'black' };
  board[6][4] = { type: 'bug', player: 'black' };
  board[6][6] = { type: 'stick', player: 'black' };

  // 放置白方棋子（上方）
  // 第0行（顶部）- 大本营
  board[0][0] = { type: 'stick', player: 'white' }; // 大本营放置虎
  board[0][2] = { type: 'bug', player: 'white' }; 
  board[0][4] = { type: 'chicken', player: 'white' }; 
  board[0][6] = { type: 'tiger', player: 'white' }; 
  return board;
};

/**
 * 创建初始游戏状态
 */
export const createInitialGameState = (
  gameMode: 'pvp' | 'pvc' = 'pvp',
  difficulty?: 'easy' | 'medium' | 'hard'
): TigerChickenGameState => {
  const board = createTigerChickenBoard();

  return {
    board,
    currentPlayer: 'black', // 黑方先行
    status: 'playing',
    winner: null,
    moves: [],
    gameHistory: [],
    gameMode,
    gameType: 'tigerchicken',
    difficulty,
    isAIThinking: false,

    // 虎鸡虫棒棋特有状态
    selectedPiece: undefined,
    possibleMoves: [],
    capturedPieces: { black: [], white: [] },

    // 大本营位置
    blackHome: { row: 6, col: 3 },
    whiteHome: { row: 0, col: 3 },

    // 游戏统计
    totalMoves: 0,
    captureMoves: 0,
    winningMoves: 0,
  };
};

// ==================== 游戏规则验证 ====================

/**
 * 虎吃鸡规则检查
 */
export const canTigerEatChicken = (
  attacker: TigerChickenPiece,
  target: TigerChickenPiece
): boolean => {
  return attacker.type === 'tiger' && target.type === 'chicken';
};

/**
 * 鸡啄虫规则检查
 */
export const canChickenEatBug = (
  attacker: TigerChickenPiece,
  target: TigerChickenPiece
): boolean => {
  return attacker.type === 'chicken' && target.type === 'bug';
};

/**
 * 虫蛀棒规则检查
 */
export const canBugEatStick = (
  attacker: TigerChickenPiece,
  target: TigerChickenPiece
): boolean => {
  return attacker.type === 'bug' && target.type === 'stick';
};

/**
 * 棒打虎规则检查
 */
export const canStickBeatTiger = (
  attacker: TigerChickenPiece,
  target: TigerChickenPiece
): boolean => {
  return attacker.type === 'stick' && target.type === 'tiger';
};

/**
 * 检查是否可以吃子
 */
export const canCapture = (
  attacker: TigerChickenPiece,
  target: TigerChickenPiece
): boolean => {
  // 检查单向克制关系
  if (canTigerEatChicken(attacker, target)) return true;
  if (canChickenEatBug(attacker, target)) return true;
  if (canBugEatStick(attacker, target)) return true;
  if (canStickBeatTiger(attacker, target)) return true;

  return false;
};

/**
 * 检查棋子移动是否合法
 */
export const canPieceMove = (
  from: Position,
  to: Position,
  piece: TigerChickenPiece,
  board: (TigerChickenPiece | null)[][]
): boolean => {
  // 不能吃自己的棋子
  const targetPiece = board[to.row][to.col];
  if (targetPiece && targetPiece.player === piece.player) {
    return false;
  }

  // 不能移动到相同位置
  if (from.row === to.row && from.col === to.col) {
    return false;
  }

  // 检查是否在棋盘内
  if (!isValidPosition(to)) {
    return false;
  }

  // 检查是否为相邻移动（只能走一格）
  if (!isAdjacentMove(from, to)) {
    return false;
  }

  // 检查是否进入自己大本营（不允许）
  if (isHomePosition(to, piece.player)) {
    return false;
  }

  // 检查是否可以移动到目标位置
  if (isSameLine(from, to, piece)) {
    // 相邻移动不需要检查路径阻挡
    // 如果目标位置有棋子，检查是否可以吃子
    if (targetPiece) {
      return canCapture(piece, targetPiece);
    }

    return true;
  }

  return false;
};

// ==================== 游戏操作 ====================

/**
 * 获取棋子的所有可能移动
 */
export const getPieceMoves = (
  gameState: TigerChickenGameState,
  position: Position
): TigerChickenMove[] => {
  const piece = gameState.board[position.row][position.col];
  if (!piece) return [];

  const moves: TigerChickenMove[] = [];

  // 检查所有可能的目标位置
  for (let row = 0; row < 7; row++) {
    for (let col = 0; col < 7; col++) {
      const to = { row, col };
      if (canPieceMove(position, to, piece, gameState.board)) {
        const targetPiece = gameState.board[row][col];
        const move: TigerChickenMove = {
          player: piece.player,
          from: position,
          to,
          captures: targetPiece ? [targetPiece] : undefined,
          isCapture: !!targetPiece,
          isWinningMove: false
        };

        // 检查是否是获胜移动（占领对方大本营）
        const opponentHome = piece.player === 'black' ? gameState.whiteHome : gameState.blackHome;
        if (to.row === opponentHome.row && to.col === opponentHome.col) {
          move.isWinningMove = true;
        }

        moves.push(move);
      }
    }
  }

  return moves;
};

/**
 * 获取玩家的所有可能移动
 */
export const getAllPossibleMoves = (
  gameState: TigerChickenGameState,
  player: Player
): TigerChickenMove[] => {
  const allMoves: TigerChickenMove[] = [];

  for (let row = 0; row < 7; row++) {
    for (let col = 0; col < 7; col++) {
      const piece = gameState.board[row][col];
      if (piece && piece.player === player) {
        const position = { row, col };
        const moves = getPieceMoves(gameState, position);
        allMoves.push(...moves);
      }
    }
  }

  return allMoves;
};

/**
 * 创建测试棋盘（用于验证移动合法性）
 */
export const makeTestMove = (
  board: (TigerChickenPiece | null)[][],
  move: TigerChickenMove
): (TigerChickenPiece | null)[][] => {
  const newBoard = board.map(row => [...row]);
  const piece = newBoard[move.from.row][move.from.col];

  if (piece) {
    newBoard[move.to.row][move.to.col] = piece;
    newBoard[move.from.row][move.from.col] = null;
  }

  return newBoard;
};

/**
 * 执行移动
 */
export const executeMove = (
  gameState: TigerChickenGameState,
  move: TigerChickenMove
): TigerChickenGameState => {
  const newBoard = gameState.board.map(row => [...row]);
  const piece = newBoard[move.from.row][move.from.col];

  if (!piece) return gameState;

  // 移动棋子
  newBoard[move.to.row][move.to.col] = piece;
  newBoard[move.from.row][move.from.col] = null;

  // 处理吃子
  const newCapturedPieces = { ...gameState.capturedPieces };
  if (move.captures) {
    const capturedPlayer = move.captures[0].player === 'black' ? 'black' : 'white';
    newCapturedPieces[capturedPlayer] = [...newCapturedPieces[capturedPlayer], ...move.captures];
  }

  // 创建新游戏状态
  const newGameState: TigerChickenGameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    moves: [...gameState.moves, move],
    gameHistory: [...gameState.gameHistory, gameState],
    capturedPieces: newCapturedPieces,

    // 更新统计
    totalMoves: gameState.totalMoves + 1,
    captureMoves: gameState.captureMoves + (move.isCapture ? 1 : 0),
    winningMoves: gameState.winningMoves + (move.isWinningMove ? 1 : 0),

    // 重置选择状态
    selectedPiece: undefined,
    possibleMoves: [],
  };

  return newGameState;
};

/**
 * 检查游戏是否结束
 */
export const checkGameEnd = (
  gameState: TigerChickenGameState
): { isEnd: boolean; winner: Player | null; reason: string } => {
  const currentPlayer = gameState.currentPlayer;
  const possibleMoves = getAllPossibleMoves(gameState, currentPlayer);

  // 检查是否有获胜移动（占领对方大本营）
  for (const move of possibleMoves) {
    if (move.isWinningMove) {
      const winner = currentPlayer;
      return {
        isEnd: true,
        winner,
        reason: `${currentPlayer === 'black' ? '黑方' : '白方'}占领对方大本营获胜！`
      };
    }
  }

  // 检查是否还有合法移动
  if (possibleMoves.length === 0) {
    return { isEnd: true, winner: null, reason: '无法移动，和棋！' };
  }

  return { isEnd: false, winner: null, reason: '' };
};

// ==================== 游戏状态管理 ====================

/**
 * 选择棋子
 */
export const selectPiece = (
  gameState: TigerChickenGameState,
  position: Position
): TigerChickenGameState => {
  const piece = gameState.board[position.row][position.col];

  if (!piece || piece.player !== gameState.currentPlayer) {
    return {
      ...gameState,
      selectedPiece: undefined,
      possibleMoves: []
    };
  }

  // 获取可能的移动
  const possibleMoves = getPieceMoves(gameState, position);

  return {
    ...gameState,
    selectedPiece: position,
    possibleMoves
  };
};

/**
 * 取消选择棋子
 */
export const deselectPiece = (gameState: TigerChickenGameState): TigerChickenGameState => {
  return {
    ...gameState,
    selectedPiece: undefined,
    possibleMoves: []
  };
};

/**
 * 悔棋功能
 */
export const undoMove = (gameState: TigerChickenGameState): TigerChickenGameState => {
  if (gameState.gameHistory.length === 0) {
    return gameState; // 没有历史记录，无法悔棋
  }

  // 获取上一个游戏状态
  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];

  return {
    ...previousState,
    selectedPiece: undefined,
    possibleMoves: [],
  };
};

/**
 * 执行移动
 */
export const makeMove = (
  gameState: TigerChickenGameState,
  move: TigerChickenMove
): TigerChickenGameState => {
  // 执行移动
  const newGameState = executeMove(gameState, move);

  // 检查游戏是否结束
  const gameEnd = checkGameEnd(newGameState);
  if (gameEnd.isEnd) {
    return {
      ...newGameState,
      status: 'finished',
      winner: gameEnd.winner
    };
  }

  return newGameState;
};

export default {
  // 基础函数
  isValidPosition,
  isHomePosition,
  isSameLine,
  isPathBlocked,
  getDistance,
  canCapture,

  // 棋盘和棋子
  createTigerChickenBoard,
  createInitialGameState,

  // 游戏规则
  canPieceMove,
  getPieceMoves,
  getAllPossibleMoves,
  checkGameEnd,

  // 游戏操作
  executeMove,
  selectPiece,
  deselectPiece,
  makeMove,
  undoMove
};